import type {
  BlendBooleanModifier,
  BlendModifier,
} from "../blend-reader/types/BlendModifier.ts";
import type { SceneObject } from "../modeling/types.ts";
import { applyModifiers } from "../viewport/modifierPipeline.ts";
import { sceneObjectGeometryCacheKey } from "../viewport/modifiers/cacheKeys.ts";
import {
  buildSceneObjectModifierGeometry,
  mergeModifierStack,
  resolveModifierEvaluation,
} from "../viewport/modifiers/evaluation.ts";
import { isManifoldReady } from "../viewport/modifiers/manifoldRuntime.ts";
import type { ModifierGeometry } from "../viewport/modifiers/modifierTypes.ts";
import {
  buildSceneObjectWorldNameMap,
  findCollectionIdForObject,
  getSceneObjectWorldTransform,
} from "./collectionGraph.ts";
import { createCollectionId } from "./collectionIds.ts";
import {
  decomposeWorldToLocalTransform,
  type EulerTransform,
  groupTransformFromEulerTransform,
} from "./groupTransform.ts";
import type { ObjectSelectionTarget } from "./objectSelection.ts";
import type { SceneObjectMap } from "./sceneObjectMap.ts";
import type {
  Collection,
  CollectionCutterRef,
  ModifierOverride,
} from "./storeTypes.ts";

interface SurfaceObjectCommitInput {
  sceneObjects: SceneObjectMap;
  collections: Map<string, Collection>;
  modifierOverrides: Map<string, ModifierOverride[]>;
  visibility: Map<string, boolean>;
  renderVisibility: Map<string, boolean>;
  cutterObject: SceneObject;
  hostObjectId: string | null;
  reverse: boolean;
}

export interface SurfaceObjectCommitResult {
  sceneObjects: SceneObjectMap;
  collections: Map<string, Collection>;
  modifierOverrides: Map<string, ModifierOverride[]>;
  visibility: Map<string, boolean>;
  renderVisibility: Map<string, boolean>;
  selectedObjectTargets: ObjectSelectionTarget[];
  booleanApplied: boolean;
}

const VOLUME_EPSILON_RATIO = 1e-7;
const POSITION_EPSILON = 1e-6;

export function commitSurfaceObjectWithOptionalBoolean({
  sceneObjects,
  collections,
  modifierOverrides,
  visibility,
  renderVisibility,
  cutterObject,
  hostObjectId,
  reverse,
}: SurfaceObjectCommitInput): SurfaceObjectCommitResult {
  if (!reverse) {
    const nextSceneObjects = new Map(sceneObjects);
    nextSceneObjects.set(cutterObject.id, cutterObject);
    return {
      sceneObjects: nextSceneObjects,
      collections,
      modifierOverrides,
      visibility,
      renderVisibility,
      selectedObjectTargets: [{ kind: "sceneObject", id: cutterObject.id }],
      booleanApplied: false,
    };
  }

  const hostObject = hostObjectId ? sceneObjects.get(hostObjectId) : undefined;
  if (!hostObject || !canUseAsBooleanOperand(hostObject)) {
    return commitVisibleSurfaceObject({
      sceneObjects,
      collections,
      modifierOverrides,
      visibility,
      renderVisibility,
      cutterObject,
    });
  }

  const hiddenCutter = {
    ...cutterObject,
    name: uniqueSceneObjectName(
      sceneObjects,
      `Cutter.${hostObject.name}.${cutterObject.name}`,
    ),
  };
  if (!canUseAsBooleanOperand(hiddenCutter)) {
    return commitVisibleSurfaceObject({
      sceneObjects,
      collections,
      modifierOverrides,
      visibility,
      renderVisibility,
      cutterObject,
    });
  }

  const nextSceneObjects = new Map(sceneObjects);
  nextSceneObjects.set(hiddenCutter.id, hiddenCutter);

  const existingOverrides = modifierOverrides.get(hostObject.id) ?? [];
  const existingStack = mergeModifierStack([], existingOverrides);
  const booleanModifier = buildSurfaceBooleanModifier(
    existingStack,
    hiddenCutter.name,
  );

  if (
    !preflightBooleanDifference({
      sceneObjects: nextSceneObjects,
      collections,
      modifierOverrides,
      hostObject,
      existingStack,
      booleanModifier,
    })
  ) {
    return commitVisibleSurfaceObject({
      sceneObjects,
      collections,
      modifierOverrides,
      visibility,
      renderVisibility,
      cutterObject,
    });
  }

  const nextOverrides = new Map(modifierOverrides);
  nextOverrides.set(hostObject.id, [
    ...existingOverrides,
    {
      enabled: true,
      runtimeModifier: booleanModifier,
      userAdded: true,
    },
  ]);

  const nextVisibility = new Map(visibility);
  nextVisibility.set(hiddenCutter.id, false);
  const nextRenderVisibility = new Map(renderVisibility);
  nextRenderVisibility.set(hiddenCutter.id, false);
  const grouped = groupSurfaceBooleanObjects({
    sceneObjects: nextSceneObjects,
    collections,
    modifierOverrides: nextOverrides,
    hostObject,
    cutterObject: hiddenCutter,
  });

  return {
    sceneObjects: grouped.sceneObjects,
    collections: grouped.collections,
    modifierOverrides: nextOverrides,
    visibility: nextVisibility,
    renderVisibility: nextRenderVisibility,
    selectedObjectTargets: grouped.selectedObjectTargets,
    booleanApplied: true,
  };
}

function commitVisibleSurfaceObject({
  sceneObjects,
  collections,
  modifierOverrides,
  visibility,
  renderVisibility,
  cutterObject,
}: Pick<
  SurfaceObjectCommitInput,
  | "sceneObjects"
  | "collections"
  | "modifierOverrides"
  | "visibility"
  | "renderVisibility"
  | "cutterObject"
>): SurfaceObjectCommitResult {
  const nextSceneObjects = new Map(sceneObjects);
  nextSceneObjects.set(cutterObject.id, cutterObject);
  return {
    sceneObjects: nextSceneObjects,
    collections,
    modifierOverrides,
    visibility,
    renderVisibility,
    selectedObjectTargets: [{ kind: "sceneObject", id: cutterObject.id }],
    booleanApplied: false,
  };
}

function canUseAsBooleanOperand(obj: SceneObject): boolean {
  return (
    obj.vertices.length >= 9 &&
    obj.indices.length >= 3 &&
    obj.topology !== undefined
  );
}

function groupSurfaceBooleanObjects({
  sceneObjects,
  collections,
  modifierOverrides,
  hostObject,
  cutterObject,
}: {
  sceneObjects: SceneObjectMap;
  collections: Map<string, Collection>;
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>;
  hostObject: SceneObject;
  cutterObject: SceneObject;
}): {
  sceneObjects: SceneObjectMap;
  collections: Map<string, Collection>;
  selectedObjectTargets: ObjectSelectionTarget[];
} {
  const existingCollectionId = findCollectionIdForObject(
    collections,
    hostObject.id,
  );
  if (existingCollectionId) {
    const collection = collections.get(existingCollectionId);
    if (!collection) {
      return {
        sceneObjects,
        collections,
        selectedObjectTargets: [{ kind: "sceneObject", id: cutterObject.id }],
      };
    }
    const cutterWorld = sceneObjectEulerTransform(cutterObject);
    const nextSceneObjects = patchObjectEulerTransform(
      sceneObjects,
      cutterObject.id,
      decomposeWorldToLocalTransform(collection.transform, cutterWorld),
    );
    const nextCollections = new Map(collections);
    const nextObjectIds = new Set(collection.objectIds);
    nextObjectIds.add(cutterObject.id);
    const { generatorRef: _generatorRef, ...bakedCollection } = collection;
    nextCollections.set(existingCollectionId, {
      ...bakedCollection,
      objectIds: nextObjectIds,
      cutterRef: nextCutterRef(collection, hostObject.id, cutterObject.id),
    });
    return {
      sceneObjects: nextSceneObjects,
      collections: nextCollections,
      selectedObjectTargets: [{ kind: "sceneObject", id: cutterObject.id }],
    };
  }

  if (hasEnabledFollowObjectModifier(modifierOverrides.get(hostObject.id))) {
    return {
      sceneObjects,
      collections,
      selectedObjectTargets: [{ kind: "sceneObject", id: cutterObject.id }],
    };
  }

  const hostWorld = sceneObjectEulerTransform(hostObject);
  const collectionTransform = groupTransformFromEulerTransform({
    position: hostWorld.position,
    rotation: hostWorld.rotation,
    scale: [1, 1, 1],
  });
  const collectionId = createCollectionId();
  const nextSceneObjects = patchObjectEulerTransform(
    patchObjectEulerTransform(
      sceneObjects,
      hostObject.id,
      decomposeWorldToLocalTransform(collectionTransform, hostWorld),
    ),
    cutterObject.id,
    decomposeWorldToLocalTransform(
      collectionTransform,
      sceneObjectEulerTransform(cutterObject),
    ),
  );
  const nextCollections = new Map(collections);
  nextCollections.set(collectionId, {
    id: collectionId,
    name: uniqueCollectionName(collections, `${hostObject.name} Cuts`),
    transform: collectionTransform,
    objectIds: new Set([hostObject.id, cutterObject.id]),
    cutterRef: {
      hostObjectId: hostObject.id,
      cutterObjectIds: new Set([cutterObject.id]),
    },
  });
  return {
    sceneObjects: nextSceneObjects,
    collections: nextCollections,
    selectedObjectTargets: [{ kind: "sceneObject", id: cutterObject.id }],
  };
}

function nextCutterRef(
  collection: Collection,
  hostObjectId: string,
  cutterObjectId: string,
): CollectionCutterRef {
  const cutterObjectIds =
    collection.cutterRef?.hostObjectId === hostObjectId
      ? new Set(collection.cutterRef.cutterObjectIds)
      : new Set<string>();
  cutterObjectIds.add(cutterObjectId);
  return {
    hostObjectId,
    cutterObjectIds,
  };
}

function sceneObjectEulerTransform(obj: SceneObject): EulerTransform {
  return {
    position: [obj.position[0], obj.position[1], obj.position[2]],
    rotation: [obj.rotation[0], obj.rotation[1], obj.rotation[2]],
    scale: [obj.scale[0], obj.scale[1], obj.scale[2]],
  };
}

function patchObjectEulerTransform(
  sceneObjects: SceneObjectMap,
  objectId: string,
  transform: EulerTransform,
): SceneObjectMap {
  const obj = sceneObjects.get(objectId);
  if (!obj) return sceneObjects;
  const next = new Map(sceneObjects);
  next.set(objectId, {
    ...obj,
    position: transform.position,
    rotation: transform.rotation,
    scale: transform.scale,
  });
  return next;
}

function hasEnabledFollowObjectModifier(
  overrides: readonly ModifierOverride[] | undefined,
): boolean {
  if (!overrides) return false;
  for (const override of overrides) {
    if (override.enabled === false) continue;
    if (override.runtimeModifier?.kind === "followObject") return true;
  }
  return false;
}

function buildSurfaceBooleanModifier(
  existingStack: readonly BlendModifier[],
  targetObjectName: string,
): BlendBooleanModifier {
  const existingBooleanCount = existingStack.filter(
    (m) => m.kind === "boolean",
  ).length;
  return {
    kind: "boolean",
    name:
      existingBooleanCount === 0
        ? "Boolean"
        : `Boolean.${String(existingBooleanCount + 1).padStart(3, "0")}`,
    enabled: true,
    operation: "difference",
    targetObjectName,
    solver: "exact",
    selfIntersect: false,
    holeTolerance: 0,
  };
}

function preflightBooleanDifference({
  sceneObjects,
  collections,
  modifierOverrides,
  hostObject,
  existingStack,
  booleanModifier,
}: {
  sceneObjects: SceneObjectMap;
  collections: ReadonlyMap<string, Collection>;
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>;
  hostObject: SceneObject;
  existingStack: readonly BlendModifier[];
  booleanModifier: BlendBooleanModifier;
}): boolean {
  if (!isManifoldReady()) return false;

  const ownerTransform = getSceneObjectWorldTransform(
    sceneObjects,
    collections,
    hostObject.id,
  ) ?? {
    position: hostObject.position,
    rotation: hostObject.rotation,
    scale: hostObject.scale,
  };
  const sceneObjectsByName = buildSceneObjectWorldNameMap(
    sceneObjects,
    collections,
  );
  const baseCacheKey = sceneObjectGeometryCacheKey(hostObject);

  try {
    const before = evaluateHostStack({
      hostObject,
      ownerTransform,
      stack: existingStack,
      sceneObjectsByName,
      modifierOverrides,
      baseCacheKey,
    });
    if (!hasFiniteGeometry(before)) return false;

    const after = evaluateHostStack({
      hostObject,
      ownerTransform,
      stack: [...existingStack, booleanModifier],
      sceneObjectsByName,
      modifierOverrides,
      baseCacheKey,
    });
    if (!hasFiniteGeometry(after)) return false;

    return booleanResultIsMaterial(before, after);
  } catch {
    return false;
  }
}

function evaluateHostStack({
  hostObject,
  ownerTransform,
  stack,
  sceneObjectsByName,
  modifierOverrides,
  baseCacheKey,
}: {
  hostObject: SceneObject;
  ownerTransform: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  };
  stack: readonly BlendModifier[];
  sceneObjectsByName: ReadonlyMap<string, SceneObject>;
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>;
  baseCacheKey: string;
}): ModifierGeometry {
  const inputGeometry = buildSceneObjectModifierGeometry(hostObject);
  const resolved = resolveModifierEvaluation(
    {
      position: ownerTransform.position,
      rotation: ownerTransform.rotation,
      scale: ownerTransform.scale,
      vertexGroups: hostObject.vertexGroups,
    },
    stack,
    sceneObjectsByName,
    modifierOverrides,
  );
  return applyModifiers(
    inputGeometry,
    resolved.modifiers,
    resolved.input,
    baseCacheKey,
  );
}

function booleanResultIsMaterial(
  before: ModifierGeometry,
  after: ModifierGeometry,
): boolean {
  if (before.positions.length === 0 || before.indices.length === 0) {
    return false;
  }
  if (after.positions.length === 0 && after.indices.length === 0) return true;
  if (geometriesNearlyEqual(before, after)) return false;

  const beforeVolume = Math.abs(signedMeshVolume(before));
  const afterVolume = Math.abs(signedMeshVolume(after));
  const diagonal = geometryDiagonal(before);
  const volumeEpsilon = Math.max(
    1e-10,
    diagonal * diagonal * diagonal * VOLUME_EPSILON_RATIO,
  );
  return (
    beforeVolume > volumeEpsilon &&
    Math.abs(beforeVolume - afterVolume) > volumeEpsilon
  );
}

function hasFiniteGeometry(geo: ModifierGeometry): boolean {
  if (geo.positions.length % 3 !== 0 || geo.indices.length % 3 !== 0) {
    return false;
  }
  for (let i = 0; i < geo.positions.length; i += 1) {
    if (!Number.isFinite(geo.positions[i])) return false;
  }
  const vertexCount = geo.positions.length / 3;
  for (let i = 0; i < geo.indices.length; i += 1) {
    const index = geo.indices[i];
    if (!Number.isInteger(index) || index < 0 || index >= vertexCount) {
      return false;
    }
  }
  return true;
}

function geometriesNearlyEqual(a: ModifierGeometry, b: ModifierGeometry) {
  if (a.positions.length !== b.positions.length) return false;
  if (a.indices.length !== b.indices.length) return false;
  for (let i = 0; i < a.indices.length; i += 1) {
    if (a.indices[i] !== b.indices[i]) return false;
  }
  for (let i = 0; i < a.positions.length; i += 1) {
    if (Math.abs(a.positions[i] - b.positions[i]) > POSITION_EPSILON) {
      return false;
    }
  }
  return true;
}

function signedMeshVolume(geo: ModifierGeometry): number {
  let volume = 0;
  const p = geo.positions;
  const indices = geo.indices;
  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i] * 3;
    const ib = indices[i + 1] * 3;
    const ic = indices[i + 2] * 3;
    const ax = p[ia];
    const ay = p[ia + 1];
    const az = p[ia + 2];
    const bx = p[ib];
    const by = p[ib + 1];
    const bz = p[ib + 2];
    const cx = p[ic];
    const cy = p[ic + 1];
    const cz = p[ic + 2];
    volume +=
      (ax * (by * cz - bz * cy) -
        ay * (bx * cz - bz * cx) +
        az * (bx * cy - by * cx)) /
      6;
  }
  return volume;
}

function geometryDiagonal(geo: ModifierGeometry): number {
  if (geo.positions.length === 0) return 0;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < geo.positions.length; i += 3) {
    const x = geo.positions[i];
    const y = geo.positions[i + 1];
    const z = geo.positions[i + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  return Math.hypot(maxX - minX, maxY - minY, maxZ - minZ);
}

function uniqueSceneObjectName(
  sceneObjects: ReadonlyMap<string, SceneObject>,
  desired: string,
): string {
  const names = new Set(Array.from(sceneObjects.values(), (o) => o.name));
  if (!names.has(desired)) return desired;
  let suffix = 2;
  while (true) {
    const candidate = `${desired}.${String(suffix).padStart(3, "0")}`;
    if (!names.has(candidate)) return candidate;
    suffix += 1;
  }
}

function uniqueCollectionName(
  collections: ReadonlyMap<string, Collection>,
  desired: string,
): string {
  const names = new Set(Array.from(collections.values(), (c) => c.name));
  if (!names.has(desired)) return desired;
  let suffix = 2;
  while (true) {
    const candidate = `${desired}.${String(suffix).padStart(3, "0")}`;
    if (!names.has(candidate)) return candidate;
    suffix += 1;
  }
}
